const TOUR_TOOLTIP_MAX_AGE = 60 * 60 * 24 * 7;
const TOUR_TOOLTIP_SCROLL_THROTTLE = 5500;
const TOUR_TARGET_HIGHLIGHT_OFFSET = 4;
const TOUR_TOOLTIP_MASK_CLASS = 'gh-tour-mask-overlay';
const TOUR_TOOLTIP_OVERLAY_CLASS = 'gh-tour-tooltip-overlay';
const TOUR_TARGET_ACTIVE_CLASS = 'gh-tour-target-active';
const TOUR_MASK_ACTIVE_CLASS = 'is-active';
const TOUR_BODY_OPEN_CLASS = 'gh-tour-open';

type TourTargetRef = {
    $el?: HTMLElement | null;
};

type TourTargetValueWrapper = {
    value?: TourTargetValue;
};

export type TourTargetValue =
    | string
    | number
    | HTMLElement
    | TourTargetRef
    | TourTargetValueWrapper
    | (() => TourTargetValue | null | undefined)
    | null
    | undefined;

export interface TourTooltipStep {
    target: TourTargetValue;
    order?: number;
}

export interface TourTooltipStartOptions {
    controlled?: boolean;
    controllerId?: string;
    steps?: TourTooltipStep[];
    mask?: boolean;
    useCookie?: boolean;
    emitCloseOnEmpty?: boolean;
    onClose?: () => void;
}

export interface TourTooltipUpdateOptions {
    controllerId?: string;
    steps?: TourTooltipStep[];
    mask?: boolean;
    useCookie?: boolean;
    onClose?: () => void;
}

export interface TourTooltipStopOptions {
    controllerId?: string;
    preserveRuntime?: boolean;
    emitClose?: boolean;
}

export interface TourTooltipCloseAllOptions {
    emitClose?: boolean;
}

export interface TourTooltipIntf {
    refresh: (options?: TourTooltipStartOptions) => void;
    start: (options?: TourTooltipStartOptions) => void;
    update: (options?: TourTooltipUpdateOptions) => void;
    stop: (options?: TourTooltipStopOptions) => void;
    closeAll: (options?: TourTooltipCloseAllOptions) => void;
    clearControllerContext: () => void;
}

interface TourTooltipCallbacks {
    onClose: (() => void) | null;
}

interface TourTooltipItem {
    index: number;
    cacheKey: string;
    targetToken: string;
    order: number;
    targetEl: HTMLElement;
    tooltipEl: HTMLElement;
}

interface NormalizedRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface ViewportRect {
    width: number;
    height: number;
}

interface MaskGeometry {
    targetRect: NormalizedRect;
    tooltipRect: NormalizedRect;
    viewportRect: ViewportRect;
    borderRadius: number;
}

interface TourTooltipPortalState {
    placeholder: Comment;
    originalStyleAttr: string | null;
}

class TourTooltipService implements TourTooltipIntf {
    private steps: TourTooltipStep[] = [];
    private items: TourTooltipItem[] = [];
    private queue: TourTooltipItem[] = [];
    private queueIndex = 0;
    private activeItem: TourTooltipItem | null = null;
    private autoCloseTimer: number | null = null;
    private isClosingActive = false;
    private sequenceEventsBound = false;
    private isControlled = false;
    private controllerId = '';
    private maskEnabled = false;
    private useCookie = true;
    private maskEl: HTMLDivElement | null = null;
    private tooltipOverlayEl: HTMLDivElement | null = null;
    private activeTargetEl: HTMLElement | null = null;
    private tooltipPortalMap = new WeakMap<
        HTMLElement,
        TourTooltipPortalState
    >();
    private callbacks: TourTooltipCallbacks = {
        onClose: null,
    };

    constructor() {
        this.onClick = this.onClick.bind(this);
        this.onScroll = this.throttle(
            this.onScroll.bind(this),
            TOUR_TOOLTIP_SCROLL_THROTTLE
        );
    }

    // 設定導覽步驟清單
    private setSteps({ steps = [] }: { steps?: TourTooltipStep[] } = {}): void {
        if (!Array.isArray(steps)) {
            this.steps = [];
            return;
        }

        this.steps = steps
            .filter((step): step is TourTooltipStep => {
                return Boolean(step && typeof step === 'object');
            })
            .map((step) => ({
                target: step.target,
                order: step.order,
            }));
    }

    // 非受控模式重新整理
    refresh(options: TourTooltipStartOptions = {}): void {
        this.destroy();
        this.init({
            controlled: false,
            emitCloseOnEmpty: false,
            ...options,
        });
    }

    // 受控模式啟動導覽
    start(options: TourTooltipStartOptions = {}): void {
        this.destroy();
        this.init({
            controlled: true,
            emitCloseOnEmpty: true,
            ...options,
        });
    }

    // 同步受控模式步驟與顯示設定
    update(options: TourTooltipUpdateOptions = {}): void {
        if (
            !this.isControlled ||
            !this.isSameController(options.controllerId)
        ) {
            return;
        }

        this.setCallbacks(options);

        if (Object.prototype.hasOwnProperty.call(options, 'steps')) {
            this.start({
                controlled: true,
                controllerId: this.controllerId,
                steps: options.steps,
                mask: Object.prototype.hasOwnProperty.call(options, 'mask')
                    ? options.mask
                    : this.maskEnabled,
                useCookie: Object.prototype.hasOwnProperty.call(
                    options,
                    'useCookie'
                )
                    ? options.useCookie
                    : this.useCookie,
                onClose: this.callbacks.onClose || undefined,
            });
            return;
        }

        if (Object.prototype.hasOwnProperty.call(options, 'useCookie')) {
            this.start({
                controlled: true,
                controllerId: this.controllerId,
                steps: this.steps,
                mask: Object.prototype.hasOwnProperty.call(options, 'mask')
                    ? options.mask
                    : this.maskEnabled,
                useCookie: options.useCookie,
                onClose: this.callbacks.onClose || undefined,
            });
            return;
        }

        if (Object.prototype.hasOwnProperty.call(options, 'mask')) {
            this.maskEnabled = Boolean(options.mask);
            this.applyMaskState();
        }
    }

    // 停止受控導覽並依需求保留 runtime 狀態
    stop(options: TourTooltipStopOptions = {}): void {
        if (
            !this.isControlled ||
            !this.isSameController(options.controllerId)
        ) {
            return;
        }

        const preserveRuntime = options.preserveRuntime !== false;
        this.closeAll({
            emitClose: options.emitClose !== false,
        });

        if (preserveRuntime) {
            this.clearControllerContext();
            return;
        }

        this.destroy();
        this.clearControllerContext();
    }

    // 依設定收集可用 target 與 tooltip 後開始顯示
    private init(options: TourTooltipStartOptions = {}): void {
        const {
            controlled = false,
            controllerId = '',
            steps,
            mask = false,
            useCookie = true,
            emitCloseOnEmpty = false,
        } = options;

        this.isControlled = Boolean(controlled);
        this.controllerId = this.isControlled ? String(controllerId || '') : '';
        this.maskEnabled = Boolean(mask);
        this.useCookie = Boolean(useCookie);
        this.setCallbacks(options);

        if (Object.prototype.hasOwnProperty.call(options, 'steps')) {
            this.setSteps({ steps });
        }

        this.collectItems();
        if (!this.items.length) {
            this.applyMaskState();
            if (emitCloseOnEmpty) {
                this.notifyClose();
            }
            return;
        }

        this.prepareQueue();
        if (!this.queue.length || this.isQueueDone()) {
            this.applyMaskState();
            if (emitCloseOnEmpty) {
                this.notifyClose();
            }
            return;
        }

        this.showItem(this.getActiveQueueItem(), { mode: 'tour' });
        this.bindSequenceEvents();
    }

    // 清除目前 tour 執行狀態
    private destroy(): void {
        this.clearAutoCloseTimer();

        this.items.forEach((item) => {
            this.hideTooltip(item.tooltipEl, item.targetEl);
        });

        this.unbindSequenceEvents();
        this.setMaskVisible(false);

        this.items = [];
        this.queue = [];
        this.queueIndex = 0;
        this.activeItem = null;
        this.isClosingActive = false;
    }

    // 關閉全部導覽提示
    closeAll(options: TourTooltipCloseAllOptions = {}): void {
        const { emitClose = true } = options;

        this.clearAutoCloseTimer();
        this.items.forEach((item) => {
            this.hideTooltip(item.tooltipEl, item.targetEl);
        });

        this.activeItem = null;
        this.queueIndex = this.queue.length;
        this.isClosingActive = false;
        this.unbindSequenceEvents();
        this.setMaskVisible(false);

        if (emitClose) {
            this.notifyClose();
        }
    }

    // 清空受控 context，避免跨頁或跨 controller 汙染
    clearControllerContext(): void {
        this.isControlled = false;
        this.controllerId = '';
        this.maskEnabled = false;
        this.useCookie = true;
        this.callbacks.onClose = null;
    }

    private setCallbacks(
        options: Pick<TourTooltipStartOptions, 'onClose'> = {}
    ): void {
        if (typeof options.onClose === 'function') {
            this.callbacks.onClose = options.onClose;
        }
    }

    private isTargetValueWrapper(
        target: TourTargetValue
    ): target is TourTargetValueWrapper {
        return (
            typeof target === 'object' &&
            target !== null &&
            Object.prototype.hasOwnProperty.call(target, 'value')
        );
    }

    private isTargetRef(target: TourTargetValue): target is TourTargetRef {
        return (
            typeof target === 'object' &&
            target !== null &&
            Object.prototype.hasOwnProperty.call(target, '$el')
        );
    }

    private isSameController(controllerId?: string): boolean {
        if (!this.isControlled || !this.controllerId) {
            return true;
        }

        return this.controllerId === String(controllerId || '');
    }

    private prepareQueue(): void {
        this.queue = this.items.filter((item) => !this.isLoaded(item.cacheKey));
        this.queueIndex = 0;
        this.queueIndex = this.findNextDisplayableQueueIndex(this.queueIndex);
    }

    private collectItems(): void {
        this.items = this.steps
            .map((step, index) => {
                const targetEl = this.resolveStepTarget(step);
                if (!targetEl || !this.isDisplayableTarget(targetEl)) {
                    return null;
                }

                const tooltipEl = this.resolveStepTooltip(targetEl);
                if (!tooltipEl) {
                    return null;
                }

                const targetToken = this.resolveStepToken(step, index);

                return {
                    index,
                    cacheKey: targetToken,
                    targetToken,
                    order: this.parseOrder(step.order ?? index),
                    targetEl,
                    tooltipEl,
                } satisfies TourTooltipItem;
            })
            .filter((item): item is TourTooltipItem => Boolean(item))
            .sort((a, b) => a.order - b.order);
    }

    private bindSequenceEvents(): void {
        if (this.sequenceEventsBound) {
            return;
        }

        document.addEventListener('click', this.onClick);
        document.addEventListener('scroll', this.onScroll);
        this.sequenceEventsBound = true;
    }

    private unbindSequenceEvents(): void {
        if (!this.sequenceEventsBound) {
            return;
        }

        document.removeEventListener('click', this.onClick);
        document.removeEventListener('scroll', this.onScroll);
        this.sequenceEventsBound = false;
    }

    private showItem(
        item: TourTooltipItem | null,
        options: { mode?: string } = {}
    ): void {
        const { mode = 'hover' } = options;
        if (!item) {
            return;
        }

        if (mode === 'tour' && !this.isDisplayableTarget(item.targetEl)) {
            this.markLoaded(item.cacheKey);
            this.queueIndex = this.findNextDisplayableQueueIndex(
                this.queueIndex + 1
            );

            const nextItem = this.getActiveQueueItem();
            if (!nextItem) {
                this.activeItem = null;
                this.unbindSequenceEvents();
                this.applyMaskState();
                this.notifyClose();
                return;
            }

            this.showItem(nextItem, { mode: 'tour' });
            return;
        }

        if (this.activeItem && this.activeItem !== item) {
            this.hideTooltip(
                this.activeItem.tooltipEl,
                this.activeItem.targetEl
            );
        }

        this.activeItem = item;

        this.setTargetActive(item.targetEl);
        this.showTooltip(item.tooltipEl);
        this.applyMaskState();
    }

    private closeActive(delay: number): void {
        if (this.isClosingActive || !this.activeItem) {
            return;
        }

        const activeItem = this.activeItem;
        const currentQueueIndex = this.getActiveQueueIndex();
        if (currentQueueIndex < 0) {
            return;
        }

        const nextQueueIndex = this.findNextDisplayableQueueIndex(
            currentQueueIndex + 1
        );
        const isLast = nextQueueIndex >= this.queue.length;
        this.isClosingActive = true;

        if (isLast) {
            this.queueIndex = this.queue.length;
            this.unbindSequenceEvents();
        }

        this.clearAutoCloseTimer();
        this.autoCloseTimer = window.setTimeout(() => {
            this.hideTooltip(activeItem.tooltipEl, activeItem.targetEl);
            this.markLoaded(activeItem.cacheKey);

            this.activeItem = null;
            this.isClosingActive = false;

            if (isLast) {
                this.applyMaskState();
                this.notifyClose();
                return;
            }

            this.queueIndex = nextQueueIndex;
            const nextItem = this.getActiveQueueItem();
            if (!nextItem) {
                this.applyMaskState();
                this.unbindSequenceEvents();
                this.notifyClose();
                return;
            }

            this.showItem(nextItem, { mode: 'tour' });
        }, delay);
    }

    private getEventTargetElement(event: Event): HTMLElement | null {
        const target = event.target;
        if (target instanceof HTMLElement) {
            return target;
        }

        if (target instanceof Text) {
            return target.parentElement;
        }

        return null;
    }

    private isInClickGuardArea(targetEl: HTMLElement | null): boolean {
        if (!targetEl) {
            return false;
        }

        try {
            return Boolean(targetEl.closest('[data-click-guard-anchor]'));
        } catch {
            return false;
        }
    }

    private shouldIgnoreClickEvent(event: Event): boolean {
        const targetEl = this.getEventTargetElement(event);
        if (!targetEl) {
            return false;
        }

        return Boolean(
            this.isInClickGuardArea(targetEl) ||
            targetEl.closest('#EMDialog') ||
            targetEl.closest('.ui-dialog') ||
            targetEl.closest('.ui-widget-overlay')
        );
    }

    private onClick(event: Event): void {
        if (!this.activeItem || this.shouldIgnoreClickEvent(event)) {
            return;
        }

        this.closeActive(this.isLastQueueItem() ? 5000 : 0);
    }

    private onScroll(): void {
        if (!this.activeItem) {
            return;
        }

        this.closeActive(5000);
    }

    private showTooltip(tooltipEl: HTMLElement | null): void {
        if (!tooltipEl) {
            return;
        }

        tooltipEl.dataset.tourActive = 'true';
        tooltipEl.classList.add('gh-tooltip-active');
        tooltipEl.classList.add('gh-tour');
    }

    private hideTooltip(
        tooltipEl: HTMLElement | null,
        targetEl?: HTMLElement | null
    ): void {
        if (!tooltipEl) {
            return;
        }

        delete tooltipEl.dataset.tourActive;
        tooltipEl.classList.remove('gh-tooltip-active');
        tooltipEl.classList.remove('gh-tour');
        this.restoreTooltipToOrigin(tooltipEl);

        if (targetEl) {
            this.clearTargetActive(targetEl);
        }
    }

    private applyMaskState(): void {
        this.setMaskVisible(Boolean(this.maskEnabled && this.activeItem));
    }

    private setMaskVisible(isVisible: boolean): void {
        if (isVisible) {
            const activeItem = this.activeItem;
            if (!activeItem) {
                return;
            }

            document.body.classList.add(TOUR_BODY_OPEN_CLASS);

            const maskGeometry = this.getMaskGeometry(activeItem);
            if (!maskGeometry) {
                this.setMaskVisible(false);
                return;
            }

            this.ensureMaskElements();
            this.updateMaskSvg(maskGeometry);
            this.mountTooltipToOverlay(activeItem.tooltipEl, maskGeometry);

            this.maskEl?.classList.add(TOUR_MASK_ACTIVE_CLASS);
            this.tooltipOverlayEl?.classList.add(TOUR_MASK_ACTIVE_CLASS);
            return;
        }

        this.restoreTooltipToOrigin(this.activeItem?.tooltipEl || null);

        if (this.maskEl) {
            this.maskEl.classList.remove(TOUR_MASK_ACTIVE_CLASS);
            this.maskEl.innerHTML = '';
        }

        if (this.tooltipOverlayEl) {
            this.tooltipOverlayEl.classList.remove(TOUR_MASK_ACTIVE_CLASS);
        }

        document.body.classList.remove(TOUR_BODY_OPEN_CLASS);
    }

    private ensureMaskElements(): void {
        if (!this.maskEl || !this.maskEl.parentNode) {
            this.maskEl = document.createElement('div');
            this.maskEl.className = TOUR_TOOLTIP_MASK_CLASS;
            this.maskEl.setAttribute('aria-hidden', 'true');
            document.body.appendChild(this.maskEl);
        }

        if (!this.tooltipOverlayEl || !this.tooltipOverlayEl.parentNode) {
            this.tooltipOverlayEl = document.createElement('div');
            this.tooltipOverlayEl.className = TOUR_TOOLTIP_OVERLAY_CLASS;
            this.tooltipOverlayEl.setAttribute('aria-hidden', 'true');
            document.body.appendChild(this.tooltipOverlayEl);
        }
    }

    private setTargetActive(targetEl: HTMLElement | null): void {
        const normalizedTarget = this.normalizeTargetElement(targetEl);
        if (!normalizedTarget || this.activeTargetEl === normalizedTarget) {
            return;
        }

        this.clearTargetActive();
        normalizedTarget.classList.add(TOUR_TARGET_ACTIVE_CLASS);
        this.activeTargetEl = normalizedTarget;
    }

    private clearTargetActive(targetEl?: HTMLElement | null): void {
        const normalizedTarget = this.normalizeTargetElement(targetEl);
        const targetToClear = normalizedTarget || this.activeTargetEl;
        if (!targetToClear) {
            return;
        }

        targetToClear.classList.remove(TOUR_TARGET_ACTIVE_CLASS);
        if (this.activeTargetEl === targetToClear) {
            this.activeTargetEl = null;
        }
    }

    private getMaskGeometry(item: TourTooltipItem): MaskGeometry | null {
        const targetEl = this.normalizeTargetElement(item.targetEl);
        if (!targetEl) {
            return null;
        }

        const viewportRect = this.getViewportRect();
        return {
            targetRect: this.expandRectByOffset(
                this.normalizeRect(targetEl.getBoundingClientRect()),
                TOUR_TARGET_HIGHLIGHT_OFFSET,
                viewportRect
            ),
            tooltipRect: this.normalizeRect(
                item.tooltipEl.getBoundingClientRect()
            ),
            viewportRect,
            borderRadius: 2,
        };
    }

    private updateMaskSvg(maskGeometry: MaskGeometry): void {
        if (!this.maskEl) {
            return;
        }

        const maskId = `gh-tour-mask-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 8)}`;
        const { targetRect, viewportRect, borderRadius } = maskGeometry;
        const targetRight = targetRect.left + targetRect.width;
        const targetBottom = targetRect.top + targetRect.height;

        this.maskEl.innerHTML = `
<svg style="width: 100%; height: 100%;">
    <defs>
        <mask id="${maskId}">
            <rect x="0" y="0" width="${viewportRect.width}" height="${viewportRect.height}" fill="white"></rect>
            <rect x="${targetRect.left}" y="${targetRect.top}" rx="${borderRadius}" width="${targetRect.width}" height="${targetRect.height}" fill="black"></rect>
        </mask>
    </defs>
    <rect x="0" y="0" width="${viewportRect.width}" height="${viewportRect.height}" fill="rgba(0,0,0,0.5)" mask="url(#${maskId})" pointer-events="none"></rect>
    <rect fill="transparent" pointer-events="auto" x="0" y="0" width="${viewportRect.width}" height="${Math.max(0, targetRect.top)}"></rect>
    <rect fill="transparent" pointer-events="auto" x="0" y="0" width="${Math.max(0, targetRect.left)}" height="${viewportRect.height}"></rect>
    <rect fill="transparent" pointer-events="auto" x="0" y="${targetBottom}" width="${viewportRect.width}" height="${Math.max(0, viewportRect.height - targetBottom)}"></rect>
    <rect fill="transparent" pointer-events="auto" x="${targetRight}" y="0" width="${Math.max(0, viewportRect.width - targetRight)}" height="${viewportRect.height}"></rect>
</svg>`;
    }

    private mountTooltipToOverlay(
        tooltipEl: HTMLElement | null,
        maskGeometry: MaskGeometry | null
    ): void {
        if (!tooltipEl || !this.tooltipOverlayEl || !maskGeometry) {
            return;
        }

        let portalState = this.tooltipPortalMap.get(tooltipEl);
        if (!portalState) {
            const parentNode = tooltipEl.parentNode;
            if (!parentNode) {
                return;
            }

            const placeholder = document.createComment('gh-tour-placeholder');
            parentNode.insertBefore(placeholder, tooltipEl);

            portalState = {
                placeholder,
                originalStyleAttr: tooltipEl.getAttribute('style'),
            };
            this.tooltipPortalMap.set(tooltipEl, portalState);
            this.tooltipOverlayEl.appendChild(tooltipEl);
        } else if (tooltipEl.parentNode !== this.tooltipOverlayEl) {
            this.tooltipOverlayEl.appendChild(tooltipEl);
        }

        tooltipEl.style.position = 'fixed';
        tooltipEl.style.left = `${Math.round(maskGeometry.tooltipRect.left)}px`;
        tooltipEl.style.top = `${Math.round(maskGeometry.tooltipRect.top)}px`;
        tooltipEl.style.right = 'auto';
        tooltipEl.style.bottom = 'auto';
        tooltipEl.style.transform = 'none';
        tooltipEl.style.pointerEvents = 'auto';
    }

    private restoreTooltipToOrigin(tooltipEl: HTMLElement | null): void {
        if (!tooltipEl) {
            return;
        }

        const portalState = this.tooltipPortalMap.get(tooltipEl);
        if (!portalState) {
            return;
        }

        const { placeholder, originalStyleAttr } = portalState;
        if (placeholder.parentNode) {
            placeholder.parentNode.insertBefore(tooltipEl, placeholder);
            placeholder.parentNode.removeChild(placeholder);
        } else if (tooltipEl.parentNode === this.tooltipOverlayEl) {
            this.tooltipOverlayEl?.removeChild(tooltipEl);
        }

        if (originalStyleAttr === null || originalStyleAttr === undefined) {
            tooltipEl.removeAttribute('style');
        } else {
            tooltipEl.setAttribute('style', originalStyleAttr);
        }

        this.tooltipPortalMap.delete(tooltipEl);
    }

    private getViewportRect(): ViewportRect {
        return {
            width: window.innerWidth || document.documentElement.clientWidth,
            height: window.innerHeight || document.documentElement.clientHeight,
        };
    }

    private normalizeRect(rect?: DOMRect | null): NormalizedRect {
        return {
            left: Number(rect?.left) || 0,
            top: Number(rect?.top) || 0,
            width: Number(rect?.width) || 0,
            height: Number(rect?.height) || 0,
        };
    }

    private expandRectByOffset(
        rect: NormalizedRect,
        offset: number,
        boundsRect?: ViewportRect
    ): NormalizedRect {
        const normalizedOffset = Number(offset) || 0;
        const expandedRect = {
            left: rect.left - normalizedOffset,
            top: rect.top - normalizedOffset,
            width: rect.width + normalizedOffset * 2,
            height: rect.height + normalizedOffset * 2,
        };

        if (!boundsRect) {
            return expandedRect;
        }

        const maxWidth = Math.max(0, Number(boundsRect.width) || 0);
        const maxHeight = Math.max(0, Number(boundsRect.height) || 0);
        const clampedLeft = Math.min(Math.max(expandedRect.left, 0), maxWidth);
        const clampedTop = Math.min(Math.max(expandedRect.top, 0), maxHeight);
        const clampedRight = Math.min(
            Math.max(expandedRect.left + expandedRect.width, 0),
            maxWidth
        );
        const clampedBottom = Math.min(
            Math.max(expandedRect.top + expandedRect.height, 0),
            maxHeight
        );

        return {
            left: clampedLeft,
            top: clampedTop,
            width: Math.max(0, clampedRight - clampedLeft),
            height: Math.max(0, clampedBottom - clampedTop),
        };
    }

    private notifyClose(): void {
        this.callbacks.onClose?.();
    }

    private getActiveQueueItem(): TourTooltipItem | null {
        this.queueIndex = this.findNextDisplayableQueueIndex(this.queueIndex);
        return this.queue[this.queueIndex] || null;
    }

    private getActiveQueueIndex(): number {
        if (!this.activeItem) {
            return -1;
        }

        return this.queue.findIndex((item) => item === this.activeItem);
    }

    private isLastQueueItem(): boolean {
        const activeIndex = this.getActiveQueueIndex();
        if (activeIndex < 0) {
            return true;
        }

        return (
            this.findNextDisplayableQueueIndex(activeIndex + 1) >=
            this.queue.length
        );
    }

    private isQueueDone(): boolean {
        return this.queueIndex >= this.queue.length;
    }

    private clearAutoCloseTimer(): void {
        if (this.autoCloseTimer === null) {
            return;
        }

        window.clearTimeout(this.autoCloseTimer);
        this.autoCloseTimer = null;
    }

    private extractTargetToken(rawTarget: TourTargetValue): string {
        if (!rawTarget) {
            return '';
        }

        if (typeof rawTarget === 'function') {
            return this.extractTargetToken(rawTarget());
        }

        if (this.normalizeTargetElement(rawTarget)) {
            return '';
        }

        if (this.isTargetValueWrapper(rawTarget)) {
            return this.extractTargetToken(rawTarget.value);
        }

        if (typeof rawTarget === 'string' || typeof rawTarget === 'number') {
            return String(rawTarget).trim();
        }

        return '';
    }

    private resolveByDatasetToken(token: string): HTMLElement | null {
        const normalizedToken = String(token || '').trim();
        if (!normalizedToken) {
            return null;
        }

        const target = document.querySelector(
            `[data-tour-target="${this.escapeAttrValue(normalizedToken)}"]`
        );
        return target instanceof HTMLElement ? target : null;
    }

    private resolveByIdOrDataset(token: string): HTMLElement | null {
        const normalizedToken = String(token || '').trim();
        if (!normalizedToken) {
            return null;
        }

        return (
            document.getElementById(normalizedToken) ||
            this.resolveByDatasetToken(normalizedToken)
        );
    }

    private resolveTargetValue(rawTarget: TourTargetValue): HTMLElement | null {
        if (!rawTarget) {
            return null;
        }

        if (typeof rawTarget === 'function') {
            return this.resolveTargetValue(rawTarget());
        }

        const normalizedElement = this.normalizeTargetElement(rawTarget);
        if (normalizedElement) {
            return normalizedElement;
        }

        if (this.isTargetValueWrapper(rawTarget)) {
            return this.resolveTargetValue(rawTarget.value);
        }

        if (typeof rawTarget === 'string' || typeof rawTarget === 'number') {
            return this.resolveByIdOrDataset(String(rawTarget));
        }

        return null;
    }

    private resolveStepTarget(step: TourTooltipStep): HTMLElement | null {
        return this.resolveTargetValue(step.target);
    }

    private resolveStepTooltip(
        targetEl: HTMLElement | null
    ): HTMLElement | null {
        if (!targetEl) {
            return null;
        }

        const tooltipEl = targetEl.querySelector('.gh-tooltip');
        return tooltipEl instanceof HTMLElement ? tooltipEl : null;
    }

    private resolveStepToken(step: TourTooltipStep, index: number): string {
        const targetValue = this.extractTargetToken(step.target);
        return String(targetValue || `tour-step-${index + 1}`).trim();
    }

    private parseOrder(rawOrder?: number): number {
        const normalizedOrder = Number(rawOrder);
        return Number.isFinite(normalizedOrder) ? normalizedOrder : 0;
    }

    private normalizeTargetElement(
        target: TourTargetValue
    ): HTMLElement | null {
        if (target instanceof HTMLElement) {
            return target;
        }

        if (this.isTargetRef(target) && target.$el instanceof HTMLElement) {
            return target.$el;
        }

        return null;
    }

    private isDisplayableTarget(target: TourTargetValue): boolean {
        const targetEl = this.normalizeTargetElement(target);
        if (!targetEl) {
            return false;
        }

        const style = window.getComputedStyle(targetEl);
        if (style.display === 'none') {
            return false;
        }

        const opacity = Number(style.opacity);
        if (Number.isFinite(opacity) && opacity <= 0) {
            return false;
        }

        return true;
    }

    private findNextDisplayableQueueIndex(startIndex = 0): number {
        if (!this.queue.length) {
            return 0;
        }

        const normalizedStart = Math.max(0, Number(startIndex) || 0);
        for (
            let index = normalizedStart;
            index < this.queue.length;
            index += 1
        ) {
            const item = this.queue[index];
            if (item && this.isDisplayableTarget(item.targetEl)) {
                return index;
            }
        }

        return this.queue.length;
    }

    private isLoaded(cacheKey: string): boolean {
        if (!this.useCookie) {
            return false;
        }

        return this.getCookieValue(`tooltip_${cacheKey}_loaded`) === 'true';
    }

    private markLoaded(cacheKey: string): void {
        if (!this.useCookie) {
            return;
        }

        document.cookie = `tooltip_${cacheKey}_loaded=true; path=/; max-age=${TOUR_TOOLTIP_MAX_AGE}`;
    }

    private getCookieValue(cookieKey: string): string {
        return (
            document.cookie
                .split('; ')
                .find((row) => row.startsWith(`${cookieKey}=`))
                ?.split('=')[1] || ''
        );
    }

    private throttle(func: () => void, delay: number): () => void {
        let lastCall = 0;

        return () => {
            const now = Date.now();
            if (now - lastCall >= delay) {
                lastCall = now;
                func();
            }
        };
    }

    private escapeAttrValue(value: string): string {
        return String(value).replace(/"/g, '\\"');
    }
}

export const tourTooltipService = new TourTooltipService();
